import { Entity } from '../entities/Entity.js';
import { Room } from '../level/Room.js';

/**
 * Collision system providing AABB and circle collision detection.
 *
 * This module contains:
 * - Collision shape type definitions
 * - Primitive collision detection functions
 * - Higher-level collision checking for entities
 * - Collision event reporting
 */

// ========== COLLISION SHAPE TYPES ==========

/**
 * Circle collision shape.
 */
export interface Circle {
  x: number;
  y: number;
  radius: number;
}

/**
 * Axis-aligned bounding box collision shape.
 * Origin at center (x, y).
 */
export interface AABB {
  x: number; // center x
  y: number; // center y
  width: number;
  height: number;
}

/**
 * Union type for collision shapes.
 */
export type CollisionShape = Circle | AABB;

// ========== SHAPE TYPE GUARDS ==========

/**
 * Check if a shape is a Circle.
 */
export function isCircle(shape: CollisionShape): shape is Circle {
  return 'radius' in shape;
}

/**
 * Check if a shape is an AABB.
 */
export function isAABB(shape: CollisionShape): shape is AABB {
  return 'width' in shape && 'height' in shape;
}

// ========== PRIMITIVE COLLISION DETECTION ==========

/**
 * Check if two circles overlap.
 *
 * @param a - First circle
 * @param b - Second circle
 * @returns true if circles overlap
 */
export function circleVsCircle(a: Circle, b: Circle): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distSq = dx * dx + dy * dy;
  const radiusSum = a.radius + b.radius;
  return distSq <= radiusSum * radiusSum;
}

/**
 * Check if a circle overlaps an AABB.
 *
 * Uses the "closest point" algorithm:
 * Find the closest point on the AABB to the circle's center,
 * then check if that point is within the circle's radius.
 *
 * @param circle - Circle shape
 * @param box - AABB shape
 * @returns true if circle overlaps AABB
 */
export function circleVsAABB(circle: Circle, box: AABB): boolean {
  // Calculate AABB bounds
  const halfWidth = box.width / 2;
  const halfHeight = box.height / 2;
  const minX = box.x - halfWidth;
  const maxX = box.x + halfWidth;
  const minY = box.y - halfHeight;
  const maxY = box.y + halfHeight;

  // Find closest point on AABB to circle center
  const closestX = Math.max(minX, Math.min(circle.x, maxX));
  const closestY = Math.max(minY, Math.min(circle.y, maxY));

  // Check distance from circle center to closest point
  const dx = circle.x - closestX;
  const dy = circle.y - closestY;
  const distSq = dx * dx + dy * dy;

  return distSq <= circle.radius * circle.radius;
}

/**
 * Check if two AABBs overlap.
 *
 * @param a - First AABB
 * @param b - Second AABB
 * @returns true if AABBs overlap
 */
export function aabbVsAABB(a: AABB, b: AABB): boolean {
  const aHalfWidth = a.width / 2;
  const aHalfHeight = a.height / 2;
  const bHalfWidth = b.width / 2;
  const bHalfHeight = b.height / 2;

  const aMinX = a.x - aHalfWidth;
  const aMaxX = a.x + aHalfWidth;
  const aMinY = a.y - aHalfHeight;
  const aMaxY = a.y + aHalfHeight;

  const bMinX = b.x - bHalfWidth;
  const bMaxX = b.x + bHalfWidth;
  const bMinY = b.y - bHalfHeight;
  const bMaxY = b.y + bHalfHeight;

  // Check if there's overlap on both axes
  return aMinX <= bMaxX && aMaxX >= bMinX && aMinY <= bMaxY && aMaxY >= bMinY;
}

/**
 * Generic collision check between any two shapes.
 *
 * @param a - First collision shape
 * @param b - Second collision shape
 * @returns true if shapes overlap
 */
export function shapesCollide(a: CollisionShape, b: CollisionShape): boolean {
  if (isCircle(a) && isCircle(b)) {
    return circleVsCircle(a, b);
  } else if (isCircle(a) && isAABB(b)) {
    return circleVsAABB(a, b);
  } else if (isAABB(a) && isCircle(b)) {
    return circleVsAABB(b, a);
  } else if (isAABB(a) && isAABB(b)) {
    return aabbVsAABB(a, b);
  }
  return false;
}

// ========== POINT IN SHAPE TESTS ==========

/**
 * Check if a point is inside a circle.
 *
 * @param px - Point X coordinate
 * @param py - Point Y coordinate
 * @param circle - Circle to test against
 * @returns true if point is inside circle
 */
export function pointInCircle(px: number, py: number, circle: Circle): boolean {
  const dx = px - circle.x;
  const dy = py - circle.y;
  const distSq = dx * dx + dy * dy;
  return distSq <= circle.radius * circle.radius;
}

/**
 * Check if a point is inside an AABB.
 *
 * @param px - Point X coordinate
 * @param py - Point Y coordinate
 * @param box - AABB to test against
 * @returns true if point is inside AABB
 */
export function pointInAABB(px: number, py: number, box: AABB): boolean {
  const halfWidth = box.width / 2;
  const halfHeight = box.height / 2;
  const minX = box.x - halfWidth;
  const maxX = box.x + halfWidth;
  const minY = box.y - halfHeight;
  const maxY = box.y + halfHeight;

  return px >= minX && px <= maxX && py >= minY && py <= maxY;
}

// ========== COLLIDABLE INTERFACE ==========

/**
 * Interface for entities that can participate in collision detection.
 */
export interface Collidable {
  /**
   * Get the collision shape for this entity.
   * Returns null if entity has no collision shape.
   */
  getCollisionShape(): CollisionShape | null;
}

// ========== COLLISION EVENT SYSTEM ==========

/**
 * Types of collision events.
 */
export type CollisionEventType = 'entity-entity' | 'entity-wall';

/**
 * Collision event between two entities.
 */
export interface EntityEntityCollision {
  type: 'entity-entity';
  entityA: Entity;
  entityB: Entity;
}

/**
 * Collision event between an entity and a wall.
 */
export interface EntityWallCollision {
  type: 'entity-wall';
  entity: Entity;
}

/**
 * Union type for all collision events.
 */
export type CollisionEvent = EntityEntityCollision | EntityWallCollision;

// ========== HIGH-LEVEL COLLISION CHECKING ==========

/**
 * Check collisions between all entities and with walls.
 *
 * This performs broad-phase collision detection for all entities,
 * returning a list of collision events for the game logic to process.
 *
 * @param entities - All entities to check
 * @param room - Room containing walls
 * @returns List of collision events
 */
export function checkCollisions(entities: Entity[], room: Room): CollisionEvent[] {
  const events: CollisionEvent[] = [];

  // Check entity vs entity collisions
  for (let i = 0; i < entities.length; i++) {
    const entityA = entities[i];

    // Skip entities that don't implement Collidable interface
    if (!('getCollisionShape' in entityA)) continue;

    const shapeA = (entityA as any).getCollisionShape() as CollisionShape | null;
    if (!shapeA) continue;

    // Check against all other entities (avoid duplicate checks with j > i)
    for (let j = i + 1; j < entities.length; j++) {
      const entityB = entities[j];

      // Skip entities that don't implement Collidable interface
      if (!('getCollisionShape' in entityB)) continue;

      const shapeB = (entityB as any).getCollisionShape() as CollisionShape | null;
      if (!shapeB) continue;

      // Check collision
      if (shapesCollide(shapeA, shapeB)) {
        events.push({
          type: 'entity-entity',
          entityA,
          entityB,
        });
      }
    }

    // Check entity vs wall collision
    if (isCircle(shapeA)) {
      if (!circleInRoom(shapeA, room)) {
        events.push({
          type: 'entity-wall',
          entity: entityA,
        });
      }
    } else if (isAABB(shapeA)) {
      if (!aabbInRoom(shapeA, room)) {
        events.push({
          type: 'entity-wall',
          entity: entityA,
        });
      }
    }
  }

  return events;
}

/**
 * Check if a circle is fully contained within the room.
 *
 * @param circle - Circle to test
 * @param room - Room bounds
 * @returns true if circle is fully inside room
 */
function circleInRoom(circle: Circle, room: Room): boolean {
  const halfWidth = room.width / 2;
  const halfHeight = room.height / 2;

  // Check if circle extends beyond any wall
  if (circle.x - circle.radius < -halfWidth) return false;
  if (circle.x + circle.radius > halfWidth) return false;
  if (circle.y - circle.radius < -halfHeight) return false;
  if (circle.y + circle.radius > halfHeight) return false;

  return true;
}

/**
 * Check if an AABB is fully contained within the room.
 *
 * @param box - AABB to test
 * @param room - Room bounds
 * @returns true if AABB is fully inside room
 */
function aabbInRoom(box: AABB, room: Room): boolean {
  const halfWidth = room.width / 2;
  const halfHeight = room.height / 2;

  const boxHalfWidth = box.width / 2;
  const boxHalfHeight = box.height / 2;

  // Check if box extends beyond any wall
  if (box.x - boxHalfWidth < -halfWidth) return false;
  if (box.x + boxHalfWidth > halfWidth) return false;
  if (box.y - boxHalfHeight < -halfHeight) return false;
  if (box.y + boxHalfHeight > halfHeight) return false;

  return true;
}
